/**
 * Sổ asset theo nhà cung cấp — logic THUẦN phía FE (đợt 260922 E3-P3). Tách khỏi file import antd để jest chạy được.
 *
 * `refAssetView` là BẢN SAO luật của backend (`admin-service/src/utils/filmRefAssets.ts`) — sửa một bên thì sửa bên kia, test hai bên
 * dùng cùng các ca. Asset là bản đăng ký của ẢNH HIỆN TẠI: dòng `ACTIVE` của ảnh cũ ⇒ "cũ", không phải "đã có".
 */
export interface RefAssetRowLike {
  id: string;
  status: string;
  sourceUrl: string;
  assetId?: string | null;
  error?: string | null;
  createdAt: string;
}

export type RefAssetState = 'none' | 'active' | 'stale' | 'processing' | 'failed' | 'unknown';
export interface RefAssetView { state: RefAssetState; assetId?: string | null; error?: string | null; rowId?: string }

/** `SUBMITTING` quá mốc này ⇒ tiến trình đã chết giữa lúc tạo ⇒ cần đối soát (khớp backend). */
const SUBMITTING_STALE_MS = 5 * 60_000;
const newestFirst = <T extends RefAssetRowLike>(rows: T[]): T[] =>
  [...rows].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

export function refAssetView(rows: RefAssetRowLike[], currentUrl: string | null | undefined, now: Date = new Date()): RefAssetView {
  if (!currentUrl) return { state: 'none' };
  const sorted = newestFirst(rows);
  const cur = sorted.find((r) => r.status === 'ACTIVE' && r.sourceUrl === currentUrl);
  if (cur) return { state: 'active', assetId: cur.assetId ?? null, rowId: cur.id };
  const same = sorted.filter((r) => r.sourceUrl === currentUrl);
  const processing = same.find((r) => r.status === 'PROCESSING');
  if (processing) return { state: 'processing', rowId: processing.id };
  const submitting = same.find((r) => r.status === 'SUBMITTING');
  if (submitting && now.getTime() - new Date(submitting.createdAt).getTime() < SUBMITTING_STALE_MS) return { state: 'processing', rowId: submitting.id };
  const stuck = same.find((r) => r.status === 'UNKNOWN' || r.status === 'SUBMITTING');
  if (stuck) return { state: 'unknown', rowId: stuck.id };
  const newest = same[0];
  if (newest?.status === 'FAILED') return { state: 'failed', error: newest.error ?? null };
  if (rows.some((r) => r.status === 'ACTIVE')) return { state: 'stale' };
  return { state: 'none' };
}

export interface AvisChip { cls: 'ok' | 'warn' | 'bad' | ''; text: string; title: string }

/** Chip cạnh chip "Asset BytePlus". Không chữ giá (User 23/09). */
export function avisChip(v: RefAssetView): AvisChip {
  switch (v.state) {
    case 'active': return { cls: 'ok', text: '✓ AVIS', title: `Ảnh bộ đồ đã đăng ký KYC trên AVIS${v.assetId ? ` (${v.assetId})` : ''}. Clip chưa dùng asset này — chờ Epic 5 (gen clip qua AVIS).` };
    case 'processing': return { cls: '', text: '⟳ AVIS', title: 'Đang đăng ký trên AVIS — thường xong trong vài giây. Tải lại để xem.' };
    case 'stale': return { cls: 'warn', text: '⚠ AVIS cũ', title: 'Asset đăng ký từ ảnh TRƯỚC; ảnh đã đổi. Đăng ký ảnh hiện tại (menu ⋯ → Đăng ký AVIS).' };
    case 'failed': return { cls: 'bad', text: '✗ AVIS', title: `Đăng ký thất bại: ${v.error || 'không rõ lý do'}. Thử lại bằng tay ở menu ⋯.` };
    case 'unknown': return { cls: 'bad', text: '? AVIS', title: 'Lượt đăng ký trước KHÔNG rõ kết cục (có thể đã tạo asset). Bấm để đối soát — không tự tạo lại.' };
    default: return { cls: '', text: '– AVIS', title: 'Chưa đăng ký ảnh này trên AVIS.' };
  }
}

/**
 * Bộ nào nút hàng loạt được gọi. `failed` KHÔNG gồm: ảnh bị từ chối thì thử lại vẫn bị, mà mỗi lần thử có thể thêm một asset vĩnh viễn
 * (AVIS không xoá được) — thử lại bằng tay từng bộ. `unknown` cần người đối soát. `processing` gồm để dòng hết giờ chờ được poll tiếp.
 */
export function needsAvisBatch(state: RefAssetState): boolean {
  return state === 'none' || state === 'stale' || state === 'processing';
}
